import { useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { FileText, Save } from 'lucide-react'
import { Organization_ } from 'src/api/db/apiOrgs'
import { useOrgsApi } from 'src/api/db/apiOrgs'
import {
  Card,
  CardContent,
} from 'src/shared/components/ui/card'
import { Button } from 'src/shared/components/ui/button'
import { Column, Row } from 'src/shared/components/Semantic/all'
import {
  FormInput,
  FormWrapper,
} from 'src/shared/components/FormComponents'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useToast } from 'src/shared/hooks/use-toast'

const billingFormSchema = z.object({
  org_number: z.string().optional(),
  vat_number: z.string().optional(),
  country_code: z.string().optional(),
  billing_email: z
    .union([z.string().email('Invalid email address'), z.literal('')])
    .optional(),
  billing_address_line1: z.string().optional(),
  billing_address_line2: z.string().optional(),
  billing_postcode: z.string().optional(),
  billing_city: z.string().optional(),
  billing_country: z.string().optional(),
  default_invoice_reference: z.string().optional(),
})

type BillingFormValues = z.infer<typeof billingFormSchema>

export const PaymentSettings = ({
  organization,
}: {
  organization: Organization_
}) => {
  const { t } = useLocalization()
  const { toast } = useToast()
  const { updateOrganization } = useOrgsApi({})

  const methods = useForm<BillingFormValues>({
    resolver: zodResolver(billingFormSchema),
    defaultValues: {
      org_number: organization?.org_number || '',
      vat_number: organization?.vat_number || '',
      country_code: organization?.country_code || '',
      billing_email: organization?.billing_email || '',
      billing_address_line1: organization?.billing_address_line1 || '',
      billing_address_line2: organization?.billing_address_line2 || '',
      billing_postcode: organization?.billing_postcode || '',
      billing_city: organization?.billing_city || '',
      billing_country: organization?.billing_country || '',
      default_invoice_reference: organization?.default_invoice_reference || '',
    },
  })

  const { reset, formState } = methods
  const isSubmitting = formState.isSubmitting

  useEffect(() => {
    if (organization) {
      reset({
        org_number: organization.org_number || '',
        vat_number: organization.vat_number || '',
        country_code: organization.country_code || '',
        billing_email: organization.billing_email || '',
        billing_address_line1: organization.billing_address_line1 || '',
        billing_address_line2: organization.billing_address_line2 || '',
        billing_postcode: organization.billing_postcode || '',
        billing_city: organization.billing_city || '',
        billing_country: organization.billing_country || '',
        default_invoice_reference: organization.default_invoice_reference || '',
      })
    }
  }, [organization, reset])

  const handleSubmit = async (values: BillingFormValues) => {
    if (!organization?.id) return

    try {
      await updateOrganization.mutateAsync({
        id: organization.id,
        ...values,
      })
      toast({
        variant: 'success',
        title: t('Settings updated', 'Inställningar uppdaterade'),
      })
    } catch (error) {
      console.error('Failed to update billing settings:', error)
      toast({
        variant: 'destructive',
        title: t(
          'Failed to update settings',
          'Kunde inte uppdatera inställningar'
        ),
      })
    }
  }

  return (
    <Column className="gap-6">
      <Card>
        <CardContent className="p-6">
          <FormWrapper methods={methods} onSubmit={handleSubmit}>
            <Column className="gap-4">
              <Row className="items-center gap-2">
                <FileText className="h-5 w-5" />
                <h2>
                  {t('Billing information', 'Faktureringsinformation')}
                </h2>
              </Row>

              <p className="text-sm text-muted-foreground">
                {t(
                  'This information will appear on your invoices.',
                  'Denna information kommer att visas på dina fakturor.'
                )}
              </p>

              <Row className="gap-4">
                <FormInput
                  label={t('Organization number', 'Organisationsnummer')}
                  name="org_number"
                  methods={methods}
                  className="flex-1"
                  placeholder={t(
                    'Enter organization number',
                    'Ange organisationsnummer'
                  )}
                />
                <FormInput
                  label={t('VAT number', 'Momsregistreringsnummer')}
                  name="vat_number"
                  methods={methods}
                  className="flex-1"
                  placeholder={t('Enter VAT number', 'Ange momsnummer')}
                />
              </Row>

              <Row className="gap-4">
                <FormInput
                  label={t('Country code', 'Landskod')}
                  name="country_code"
                  methods={methods}
                  className="flex-1"
                  placeholder={t('Enter country code', 'Ange landskod')}
                />
                <FormInput
                  label={t('Billing email', 'Fakturerings-e-post')}
                  name="billing_email"
                  methods={methods}
                  type="email"
                  className="flex-1"
                  placeholder={t(
                    'Enter billing email',
                    'Ange fakturerings-e-post'
                  )}
                />
              </Row>

              <FormInput
                label={t('Billing address line 1', 'Faktureringsadress rad 1')}
                name="billing_address_line1"
                methods={methods}
                className="w-full"
                placeholder={t(
                  'Enter billing address line 1',
                  'Ange faktureringsadress rad 1'
                )}
              />

              <FormInput
                label={t('Billing address line 2', 'Faktureringsadress rad 2')}
                name="billing_address_line2"
                methods={methods}
                className="w-full"
                placeholder={t(
                  'Enter billing address line 2',
                  'Ange faktureringsadress rad 2'
                )}
              />

              <Row className="gap-4">
                <FormInput
                  label={t('Postal code', 'Postnummer')}
                  name="billing_postcode"
                  methods={methods}
                  className="flex-1"
                  placeholder={t('Enter postal code', 'Ange postnummer')}
                />
                <FormInput
                  label={t('City', 'Stad')}
                  name="billing_city"
                  methods={methods}
                  className="flex-1"
                  placeholder={t('Enter city', 'Ange stad')}
                />
              </Row>

              <FormInput
                label={t('Billing country', 'Faktureringsland')}
                name="billing_country"
                methods={methods}
                className="w-full"
                placeholder={t(
                  'Enter billing country',
                  'Ange faktureringsland'
                )}
              />

              <FormInput
                label={t(
                  'Default invoice reference',
                  'Standard fakturareferens'
                )}
                name="default_invoice_reference"
                methods={methods}
                className="w-full"
                placeholder={t(
                  'Enter default invoice reference',
                  'Ange standard fakturareferens'
                )}
              />

              <Row className="gap-2 justify-end mt-4">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isSubmitting}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {t('Save changes', 'Spara ändringar')}
                </Button>
              </Row>
            </Column>
          </FormWrapper>
        </CardContent>
      </Card>
    </Column>
  )
}
